import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

import { 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Shield, 
  Settings, 
  Activity, 
  Calendar,
  Edit3,
  Save,
  X,
  Upload,
  Download
} from 'lucide-react';
import api, { profileAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';

interface AdminProfileData {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  address?: string;
  role: string;
  registrationNumber?: string;
  profileImageUrl?: string;
  isEmailVerified: boolean;
  isApproved: boolean;
  profileCompleted: number;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt: string;
}

const AdminProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth(); // Add auth context
  const [profile, setProfile] = useState<AdminProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<AdminProfileData>>({});
  const [signedProfileImageUrl, setSignedProfileImageUrl] = useState<string | null>(null);
  
  // Debug logging
  console.log('AdminProfile - isAuthenticated:', isAuthenticated);
  console.log('AdminProfile - user:', user);

  // Fetch profile data
  useEffect(() => {
    if (isAuthenticated && user) {
      console.log('AdminProfile - User authenticated, fetching profile...'); // Debug log
      fetchProfile();
    } else {
      console.log('AdminProfile - User not authenticated, skipping profile fetch'); // Debug log
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  // Get signed URL for profile image
  const getSignedProfileImageUrl = async (imageUrl: string) => {
    try {
      if (!imageUrl) return null;
      
      // Extract S3 key from URL
      const url = new URL(imageUrl);
      const key = url.pathname.substring(1); // Remove leading slash
      
      console.log('Getting signed URL for admin profile image key:', key);
      
      const response = await api.get(`/upload/file/${encodeURIComponent(key)}`);
      
      if (response.data.success && response.data.url) {
        console.log('Got signed admin profile image URL:', response.data.url);
        return response.data.url;
      }
      
      return imageUrl; // Fallback to original URL
    } catch (error) {
      console.error('Error getting signed admin profile image URL:', error);
      return imageUrl; // Fallback to original URL
    }
  };

  // Update signed profile image URL when profile changes
  useEffect(() => {
    if (profile?.profileImageUrl) {
      getSignedProfileImageUrl(profile.profileImageUrl).then(url => {
        setSignedProfileImageUrl(url);
      });
    } else {
      setSignedProfileImageUrl(null);
    }
  }, [profile?.profileImageUrl]);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      
      // Try using profileAPI first, fallback to direct api call
      let response;
      if (profileAPI && typeof profileAPI.getProfile === 'function') {
        response = await profileAPI.getProfile();
      } else {
        response = await api.get('/profile');
        response = response.data;
      }
      
      if (response.success && response.data) {
        setProfile(response.data);
        setFormData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch profile');
      }
    } catch (err: any) {
      console.error('Profile fetch error:', err);
      setError('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save profile
  const saveProfile = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      const response = await profileAPI.updateProfile(formData);
      if (response.success && response.data) {
        setProfile(response.data);
        setIsEditing(false);
        setSuccess('Profile updated successfully');
      } else {
        throw new Error(response.message || 'Failed to update profile');
      }
      
    } catch (err: any) {
      setError('Failed to update profile');
      console.error('Profile update error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Cancel editing
  const cancelEditing = () => {
    setFormData(profile || {});
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  // Handle file upload
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await profileAPI.uploadProfileImage(file);

      if (response.success) {
        setSuccess('New profile image uploaded successfully.');
        fetchProfile(); // Refresh profile data
      } else {
        throw new Error(response.message || 'Failed to upload profile image');
      }
    } catch (err: any) {
      if (err.response && err.response.status === 413) {
        setError(err.response.data.message || 'File is too large. Maximum size is 10MB.');
      } else {
        setError('Failed to upload profile image. Please try again.');
      }
      console.error('File upload error:', err);
    } finally {
      setSaving(false);
      // Reset file input to allow re-uploading the same file
      event.target.value = '';
    }
  };

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-red-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <Shield className="h-10 w-10 text-red-500" />
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Authentication Required</h3>
            <p className="text-gray-600 leading-relaxed">Please log in to access your admin profile</p>
          </div>
          <Button onClick={() => navigate('/login')} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Check if user has admin role
  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-orange-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <Shield className="h-10 w-10 text-orange-500" />
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Access Denied</h3>
            <p className="text-gray-600 leading-relaxed">You don't have permission to access the admin profile</p>
            <p className="text-sm text-gray-500 bg-gray-100 px-3 py-2 rounded-lg inline-block">Current role: {user.role}</p>
          </div>
          <Button onClick={() => navigate('/')} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
            <div className="absolute inset-0 rounded-full border-4 border-blue-200 animate-pulse"></div>
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Loading Profile</h3>
            <p className="text-gray-600 leading-relaxed">Preparing your admin workspace...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center px-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="p-6 bg-red-50 rounded-2xl w-20 h-20 mx-auto flex items-center justify-center shadow-lg">
            <User className="h-10 w-10 text-red-500" />
          </div>
          <div className="space-y-3">
            <h3 className="text-2xl font-bold text-secondary">Profile Not Found</h3>
            <p className="text-gray-600 leading-relaxed">Unable to load your profile information</p>
          </div>
          <Button onClick={fetchProfile} className="bg-primary hover:bg-primary-dark text-white px-8 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Header */}
      <div className="bg-surface shadow-lg border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center space-y-4 lg:space-y-0">
            <div className="space-y-2">
              <h1 className="text-4xl font-bold text-secondary">
                Admin Profile
              </h1>
              <p className="text-gray-600 text-lg">System administration and platform management</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
              <Link to="/admin/dashboard">
                <Button variant="outline" className="w-full sm:w-auto bg-surface border-gray-300 hover:bg-background px-6 py-3 rounded-xl font-semibold transition-all duration-300">
                  <Activity className="w-5 h-5 mr-2" />
                  Dashboard
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Success/Error Messages */}
        {success && (
          <div className="mb-8 bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-primary rounded-full mr-3"></div>
              {success}
            </div>
          </div>
        )}
        {error && (
          <div className="mb-8 bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm">
            <div className="flex items-center">
              <div className="w-5 h-5 bg-red-500 rounded-full mr-3"></div>
              {error}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Profile Info & Stats */}
          <div className="lg:col-span-1 space-y-8">
            {/* Profile Photo */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardContent className="p-8">
                <div className="text-center space-y-6">
                  <div className="relative w-48 h-48 mx-auto group">
                    <label htmlFor="profile-image-upload" className="cursor-pointer">
                      <div className="w-48 h-48 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white text-6xl font-bold transition-all duration-300 group-hover:brightness-90">
                        {signedProfileImageUrl ? (
                          <img
                            src={signedProfileImageUrl}
                            alt="Profile"
                            className="w-full h-full rounded-full object-cover"
                          />
                        ) : (
                          <User size={80} />
                        )}
                      </div>
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 rounded-full flex items-center justify-center transition-all duration-300">
                        <Edit3 className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transform scale-75 group-hover:scale-100 transition-all duration-300" />
                      </div>
                    </label>
                    <input
                      type="file"
                      id="profile-image-upload"
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e)}
                    />
                  </div>
                  <div className="space-y-3">
                    <h2 className="text-3xl font-bold text-secondary">
                      {profile.firstName} {profile.lastName}
                    </h2>
                    <Badge className="bg-primary-light text-blue-800 px-4 py-2 text-sm font-semibold rounded-full">
                      System Administrator
                    </Badge>
                    {profile.registrationNumber && (
                      <div className="flex items-center justify-center space-x-2 p-3 bg-background rounded-lg">
                        <span className="text-sm font-semibold text-gray-600">Registration #:</span>
                        <span className="text-lg font-bold text-primary font-mono">{profile.registrationNumber}</span>
                      </div>
                    )}
                    <p className="text-gray-600 text-lg">Full platform access and management</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="bg-background px-8 py-6">
                <CardTitle className="text-xl font-bold text-secondary">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="flex items-center space-x-4 p-4 bg-background rounded-xl">
                  <div className="p-3 bg-primary-light rounded-full">
                    <Mail className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Email</p>
                    <p className="text-secondary font-medium">{profile.email}</p>
                  </div>
                </div>
                {profile.phone && (
                  <div className="flex items-center space-x-4 p-4 bg-background rounded-xl">
                    <div className="p-3 bg-primary-light rounded-full">
                      <Phone className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Phone</p>
                      <p className="text-secondary font-medium">{profile.phone}</p>
                    </div>
                  </div>
                )}
                {profile.address && (
                  <div className="flex items-center space-x-4 p-4 bg-background rounded-xl">
                    <div className="p-3 bg-purple-100 rounded-full">
                      <MapPin className="w-6 h-6 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Address</p>
                      <p className="text-secondary font-medium">{profile.address}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Account Status */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="bg-background px-8 py-6">
                <CardTitle className="text-xl font-bold text-secondary">Account Status</CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${profile.isEmailVerified ? 'bg-primary' : 'bg-yellow-500'}`}></div>
                    <span className="text-gray-700 font-medium">Email Verified</span>
                  </div>
                  <Badge className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    profile.isEmailVerified 
                      ? 'bg-primary-light text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {profile.isEmailVerified ? "✓ Verified" : "⚠ Pending"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <div className="flex items-center space-x-3">
                    <div className={`w-3 h-3 rounded-full ${profile.isApproved ? 'bg-primary' : 'bg-yellow-500'}`}></div>
                    <span className="text-gray-700 font-medium">Account Status</span>
                  </div>
                  <Badge className={`px-4 py-2 text-sm font-semibold rounded-full ${
                    profile.isApproved 
                      ? 'bg-primary-light text-green-800' 
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {profile.isApproved ? "✓ Active" : "⚠ Pending"}
                  </Badge>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Profile Complete</span>
                  <div className="flex items-center space-x-3">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: `${profile.profileCompleted}%` }}
                      ></div>
                    </div>
                    <span className="text-2xl font-bold text-primary">{profile.profileCompleted}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Account Statistics */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="bg-background px-8 py-6">
                <CardTitle className="text-xl font-bold text-secondary">Account Statistics</CardTitle>
              </CardHeader>
              <CardContent className="p-8 space-y-6">
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Last Login</span>
                  <span className="font-semibold text-secondary bg-surface px-4 py-2 rounded-lg">
                    {profile.lastLoginAt ? new Date(profile.lastLoginAt).toLocaleDateString() : 'Never'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Member Since</span>
                  <span className="font-semibold text-secondary bg-surface px-4 py-2 rounded-lg">
                    {new Date(profile.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex justify-between items-center p-4 bg-background rounded-xl">
                  <span className="text-gray-700 font-medium">Last Updated</span>
                  <span className="font-semibold text-secondary bg-surface px-4 py-2 rounded-lg">
                    {new Date(profile.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Admin Information */}
          <div className="lg:col-span-2 space-y-8">
            {/* Admin Information */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="bg-background px-8 py-6">
                <CardTitle className="text-xl font-bold text-secondary">Admin Information</CardTitle>
                <CardDescription className="text-gray-600">System administration details and permissions</CardDescription>
              </CardHeader>
              <CardContent className="p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <Label htmlFor="firstName" className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      First Name
                    </Label>
                    <div className="p-4 bg-background rounded-xl">
                      <p className="text-secondary font-semibold text-lg">{profile.firstName}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="lastName" className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Last Name
                    </Label>
                    <div className="p-4 bg-background rounded-xl">
                      <p className="text-secondary font-semibold text-lg">{profile.lastName}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="phone" className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Phone Number
                    </Label>
                    <div className="p-4 bg-background rounded-xl">
                      <p className="text-secondary font-semibold text-lg">{profile.phone || 'Not provided'}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <Label htmlFor="address" className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Address
                    </Label>
                    <div className="p-4 bg-background rounded-xl">
                      <p className="text-secondary font-semibold text-lg">{profile.address || 'Not provided'}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* System Permissions */}
            <Card className="border-border shadow-lg rounded-2xl overflow-hidden">
              <CardHeader className="bg-background px-8 py-6">
                <CardTitle className="text-xl font-bold text-secondary">System Permissions</CardTitle>
                <CardDescription className="text-gray-600">Your administrative access levels</CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start space-x-4 p-6 bg-green-50 rounded-2xl border border-green-200">
                    <div className="p-3 bg-primary-light rounded-full">
                      <Shield className="w-6 h-6 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <p className="font-bold text-green-900 text-lg">User Management</p>
                      <p className="text-green-700 leading-relaxed">Approve, reject, and manage all platform users</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-4 p-6 bg-blue-50 rounded-2xl border border-blue-200">
                    <div className="p-3 bg-primary-light rounded-full">
                      <Calendar className="w-6 h-6 text-primary" />
                    </div>
                    <div className="space-y-2">
                      <p className="font-bold text-blue-900 text-lg">Event Management</p>
                      <p className="text-blue-700 leading-relaxed">Create, edit, and approve all platform events</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 bg-purple-50 rounded-lg">
                    <Activity className="w-5 h-5 text-purple-600" />
                    <div>
                      <p className="font-medium text-purple-900">Analytics Access</p>
                      <p className="text-sm text-purple-700">View platform statistics and reports</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 bg-orange-50 rounded-lg">
                    <Settings className="w-5 h-5 text-orange-600" />
                    <div>
                      <p className="font-medium text-orange-900">System Settings</p>
                      <p className="text-sm text-orange-700">Configure platform settings</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="border-slate-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-slate-900">Recent Activity</CardTitle>
                <CardDescription>Your latest administrative actions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">Profile Updated</p>
                      <p className="text-sm text-slate-600">You updated your profile information</p>
                    </div>
                    <span className="text-sm text-slate-500">Just now</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">System Login</p>
                      <p className="text-sm text-slate-600">You logged into the admin panel</p>
                    </div>
                    <span className="text-sm text-slate-500">2 hours ago</span>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">Dashboard Access</p>
                      <p className="text-sm text-slate-600">You viewed the admin dashboard</p>
                    </div>
                    <span className="text-sm text-slate-500">1 day ago</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminProfile; 